@if (!loading) {
  <div class="content-block light-control-host profile-page">
    <h1>My Profile</h1>
    <p class="content-block-description">View your profile information.</p>
    <!-- Notice for admins viewing accounts they're not a member of -->
    @if (isReadOnly) {
      <div class="notice-block">
        <i class="fa-solid fa-eye"></i>
        You have access to view this account but are not a member. Profile information is not available.
      </div>
    }
    <!-- Personal Information Section (only shown for account members) -->
    @if (!isReadOnly) {
      <div class="account-settings-section">
        <h2>Personal Information</h2>
        <!-- Email (Read-only) -->
        <div class="form-group margin-top">
          <div>
            <label for="email">Email Address</label>
          </div>
          <div>
            <input
              type="email"
              id="email"
              class="form-control arpio-input"
              [value]="userProfileData.email"
              readonly
              disabled
              title="Email cannot be changed"
            />
          </div>
          <div>
            <small class="form-text text-muted">
              Your email address cannot be changed. Contact your administrator if you need to update this.
            </small>
          </div>
        </div>
        <!-- Name (Editable for native Arpio users; SSO names are managed by the provider) -->
        <form [formGroup]="nameModel" (ngSubmit)="updateName()">
          <div class="form-group margin-top">
            <label for="name">Display Name</label>
            <div class="input-group">
              <input
                type="text"
                id="name"
                class="form-control arpio-input"
                formControlName="name"
                [readonly]="!canEditName"
                [disabled]="!canEditName"
                [class.is-invalid]="nameModel.get('name')?.invalid && nameModel.get('name')?.touched"
              />
              @if (canEditName) {
                <div class="input-group-append">
                  <button
                    type="submit"
                    busy-button
                    [busy]="updatingName"
                    [done]="updatedName"
                    class="arpio-button margin-top"
                    [disabled]="saveNameDisabled"
                  >
                    UPDATE
                  </button>
                </div>
              }
            </div>
            @if (nameModel.get('name')?.invalid && nameModel.get('name')?.touched) {
              <div class="invalid-feedback">Name is required</div>
            }
            @if (!canEditName) {
              <small class="form-text text-muted">
                <em>Your name is managed by your SSO provider and cannot be changed here.</em>
              </small>
            }
            @if (updatedName) {
              <small class="form-text text-success">
                <i class="fa-regular fa-check"></i> Name updated successfully
              </small>
            }
          </div>
        </form>
      </div>
      <hr />
      <!-- Roles Section -->
      @if (userProfileData.roles.length > 0 || userProfileData.ssoRoles.length > 0) {
        <div class="account-settings-section margin-top">
          <h2>Roles</h2>
          <p class="section-description">Your assigned roles in this account.</p>
          <div class="roles-list">
            <!-- Manual/Group roles -->
            @for (role of userProfileData.roles; track role) {
              <span
                class="role-tag"
                [pTooltip]="
                  role.groupNames.length > 0 ? 'Inherited from group(s): ' + role.groupNames.join(', ') : null
                "
                tooltipPosition="top"
              >
                @if (role.isFromGroup) {
                  <i class="fa-utility-duo fa-semibold fa-users"></i>
                }
                <span>{{ role.name }}</span>
              </span>
            }
            <!-- SSO roles -->
            @for (role of userProfileData.ssoRoles; track role) {
              <span class="role-tag sso-tag" pTooltip="From SSO" tooltipPosition="top">
                <i class="fa-utility-duo fa-semibold fa-id-card sso-icon"></i>
                <span>{{ role.name }}</span>
              </span>
            }
          </div>
          @if (userProfileData.roles.length === 0 && userProfileData.ssoRoles.length === 0) {
            <em class="no-data-text">You do not have any assigned roles.</em>
          }
        </div>
      }
      <!-- Groups Section -->
      @if (userProfileData.groups.length > 0 || userProfileData.ssoGroups.length > 0) {
        <div class="account-settings-section margin-top">
          <h2>Groups</h2>
          <p class="section-description">Your group memberships in this account.</p>
          <div class="groups-list">
            <!-- Manual groups -->
            @for (group of userProfileData.groups; track group) {
              <span class="group-tag">
                {{ group }}
              </span>
            }
            <!-- SSO groups -->
            @for (group of userProfileData.ssoGroups; track group) {
              <span class="group-tag sso-tag" pTooltip="From SSO" tooltipPosition="top">
                <i class="fa-utility-duo fa-semibold fa-id-card sso-icon"></i>
                <span>{{ group.name }}</span>
              </span>
            }
          </div>
          @if (userProfileData.groups.length === 0 && userProfileData.ssoGroups.length === 0) {
            <em class="no-data-text">You do not have any assigned groups.</em>
          }
        </div>
      }
    }
  </div>
}

<!-- Loading State -->
@if (loading) {
  <div class="content-block">
    <h1><i class="fa-solid fa-spinner fa-spin"></i> Loading Profile...</h1>
  </div>
}
